import { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'interactive' | 'elevated';
  onClick?: () => void;
  header?: ReactNode;
}

export function Card({ 
  children, 
  className, 
  variant = 'default',
  onClick,
  header
}: CardProps) {
  const baseStyles = 'bg-white rounded-xl overflow-hidden';
  
  const variants = {
    default: 'shadow-sm border border-gray-100',
    interactive: 'shadow-sm border border-gray-100 cursor-pointer active:scale-[0.99] transition-transform',
    elevated: 'shadow-lg border border-gray-50',
  };

  if (header) {
    return (
      <div className={cn(baseStyles, variants[variant], className)}>
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 font-semibold">
          {header}
        </div>
        <div className="p-0">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn(baseStyles, variants[variant], className)}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}

export function CardHeader({ children, className, action }: CardHeaderProps) {
  return (
    <div className={cn(
      'bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 flex items-center justify-between',
      className
    )}>
      <span className="font-semibold">{children}</span>
      {action && <div>{action}</div>}
    </div>
  );
}

interface CardContentProps {
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}

export function CardContent({ children, className, noPadding }: CardContentProps) {
  return (
    <div className={cn(noPadding ? '' : 'p-4', className)}>
      {children}
    </div>
  );
}
